import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { X, Sparkles, Loader2, Send } from 'lucide-react';
import { ApiError, publicApi } from '../lib/apiClient';
import {
  contactFieldDomId,
  validateContactEmail,
  validateContactForm,
  validateContactMessage,
  validateContactName,
  validateContactPhone,
  type ContactFieldKey,
} from '../lib/contactValidation';
import './public-legal-contact.css';

interface ContactUsFormProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const INITIAL: FormState = { name: '', email: '', phone: '', message: '' };

/**
 * Contact Us modal. Triggered from the Footer. Single-step form: Name, Email, Phone (optional),
 * Message. Validation mirrors the Candidate Profile validators (name + phone) and the
 * Hiring Intake email regex; backend re-validates identically.
 *
 * NO throttling on the backend endpoint by locked decision — revisit if spam appears.
 */
export function ContactUsForm({ isOpen, onClose }: ContactUsFormProps) {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Partial<Record<ContactFieldKey, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset state every time the modal is reopened so a prior submission's thank-you panel
  // doesn't reappear on the next open.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setForm(INITIAL);
      setErrors({});
      setSubmitting(false);
      setSubmitted(false);
      setSubmitError(null);
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clear that field's error live as the user types.
    setErrors((prev) => {
      if (!prev[key as ContactFieldKey]) return prev;
      const next = { ...prev };
      delete next[key as ContactFieldKey];
      return next;
    });
  }

  function validateField(field: ContactFieldKey, value: string) {
    let err: string | null = null;
    if (field === 'name') err = validateContactName(value);
    else if (field === 'email') err = validateContactEmail(value);
    else if (field === 'phone') err = validateContactPhone(value);
    else if (field === 'message') err = validateContactMessage(value);
    setErrors((prev) => {
      const next = { ...prev };
      if (err) next[field] = err;
      else delete next[field];
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const invalid = validateContactForm(form);
    if (invalid) {
      setErrors((prev) => ({ ...prev, [invalid.field]: invalid.message }));
      const el = document.getElementById(contactFieldDomId(invalid.field));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el.querySelector('input, textarea') as HTMLElement | null)?.focus();
      }
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await publicApi.submitInquiry({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        message: form.message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError('Could not submit your message. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="qh-contact fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-brand-primary/20 backdrop-blur-sm"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl p-12 text-center"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-8 right-8 p-2 hover:bg-gray-50 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-300" />
          </button>
          <div className="w-20 h-20 bg-brand-primary/5 rounded-full flex items-center justify-center mx-auto mb-8">
            <Sparkles className="w-10 h-10 text-brand-primary" />
          </div>
          <h3 className="text-3xl font-display font-bold text-brand-primary mb-4">Thank you</h3>
          <p className="text-brand-muted font-medium mb-10 leading-relaxed">
            We&apos;ve received your message and will get back to you at{' '}
            <span className="text-brand-primary font-bold">{form.email}</span> shortly.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-4 bg-brand-primary text-white rounded-xl font-bold text-xs uppercase tracking-[0.15em] hover:brightness-110 transition-all"
          >
            Got it, thanks
          </button>
        </motion.div>
      </div>
    );
  }

  const errorClass = (field: ContactFieldKey) =>
    errors[field]
      ? 'border-red-400 focus:border-red-500'
      : 'border-gray-100 focus:border-brand-primary';

  const inlineError = (field: ContactFieldKey) =>
    errors[field] ? (
      <p className="text-xs text-red-600 font-medium mt-1">{errors[field]}</p>
    ) : null;

  return (
    <div className="qh-contact fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-brand-primary/80 backdrop-blur-md"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.98, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98, y: 10 }}
        className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="flex items-start justify-between px-8 pt-8 pb-4">
          <div>
            <h2 className="text-2xl font-display font-bold text-brand-primary">Contact us</h2>
            <p className="text-sm text-brand-muted mt-1 font-medium">
              Tell us how we can help — we&apos;ll get back to you shortly.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 hover:bg-gray-50 rounded-xl transition-colors -mr-2"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col overflow-hidden">
          <div className="px-8 py-6 space-y-5 overflow-y-auto">
            <div id={contactFieldDomId('name')}>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-brand-muted mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                onBlur={(e) => validateField('name', e.target.value)}
                maxLength={100}
                className={`w-full px-4 py-3 bg-white border rounded-xl text-sm font-medium text-brand-primary placeholder-gray-300 focus:outline-none transition-colors ${errorClass('name')}`}
                placeholder="Your full name"
                aria-invalid={errors.name ? true : false}
              />
              {inlineError('name')}
            </div>

            <div id={contactFieldDomId('email')}>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-brand-muted mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                onBlur={(e) => validateField('email', e.target.value)}
                maxLength={255}
                className={`w-full px-4 py-3 bg-white border rounded-xl text-sm font-medium text-brand-primary placeholder-gray-300 focus:outline-none transition-colors ${errorClass('email')}`}
                placeholder="you@example.com"
                aria-invalid={errors.email ? true : false}
              />
              {inlineError('email')}
            </div>

            <div id={contactFieldDomId('phone')}>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-brand-muted mb-2">
                Phone <span className="text-gray-300 font-medium normal-case tracking-normal">(optional)</span>
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                onBlur={(e) => validateField('phone', e.target.value)}
                maxLength={20}
                className={`w-full px-4 py-3 bg-white border rounded-xl text-sm font-medium text-brand-primary placeholder-gray-300 focus:outline-none transition-colors ${errorClass('phone')}`}
                placeholder="+1 (555) 123-4567"
                aria-invalid={errors.phone ? true : false}
              />
              {inlineError('phone')}
            </div>

            <div id={contactFieldDomId('message')}>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-brand-muted mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                onBlur={(e) => validateField('message', e.target.value)}
                maxLength={2000}
                rows={6}
                className={`w-full px-4 py-3 bg-white border rounded-xl text-sm font-medium text-brand-primary placeholder-gray-300 focus:outline-none transition-colors resize-none ${errorClass('message')}`}
                placeholder="How can we help?"
                aria-invalid={errors.message ? true : false}
              />
              <div className="flex justify-between mt-1">
                <div>{inlineError('message')}</div>
                <p className="text-xs text-gray-300 font-medium">{form.message.length}/2000</p>
              </div>
            </div>

            {submitError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 font-medium">
                {submitError}
              </div>
            )}
          </div>

          <div className="px-8 py-5 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-5 py-3 rounded-xl text-xs font-bold uppercase tracking-[0.15em] text-brand-muted hover:text-brand-primary transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-xl text-xs font-bold uppercase tracking-[0.15em] hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send message
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
